import { Component, computed, Input, OnInit, signal } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatButtonToggleModule } from "@angular/material/button-toggle";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatTableModule } from "@angular/material/table";
import { MatTooltipModule } from "@angular/material/tooltip";
import { RouterLink } from "@angular/router";
import { decimalNumber } from "@app/regex";
import {
  deliveryZonesFor,
  highestZoneFee,
  type ZoneFees,
  zoneControlName,
} from "@components/us-delivery-zones";
import { IApiResponse } from "@models/api-response.model";
import { AppStateService, RequestService } from "@services/index";

interface IVendorDeliveryInfo {
  iso2: string;
  deliveryText: string;
  standardDeliveryFee: number;
  minSubtotalEnabled?: boolean;
  minSubtotalValue?: number;
  /** US only: per-zone fees, {"1": n, ..., "5": n}. */
  deliveryZoneFees?: ZoneFees | null;
  /** Shown to a shopper collecting in person; empty means this vendor offers none. */
  walkInText?: string;
  /**
   * Who delivers in this market. "merchant" (Merchant Self Delivery) shows the
   * delivery fees on the merchant's order email and portal orders; "fishyhub"
   * (the default) does not.
   */
  deliveryMode?: "fishyhub" | "merchant";
}

interface DeliveryTableRow {
  iso2: string;
  formGroup: FormGroup;
}

@Component({
  selector: "fh-vendor-delivery-info",
  templateUrl: "./vendor-delivery-info.component.html",
  styleUrls: ["./vendor-delivery-info.component.scss"],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCheckboxModule,
    MatSelectModule,
    MatTableModule,
    MatTooltipModule,
    RouterLink,
  ],
})
export class VendorDeliveryInfoComponent implements OnInit {
  @Input() vendorId!: string;

  deliveryRows = signal<DeliveryTableRow[]>([]);
  destinationCountries = computed(() => this.appStateService.destinationCountries());
  displayedColumns: string[] = [
    "iso2",
    "deliveryMode",
    "deliveryText",
    "standardDeliveryFee",
    "walkInText",
    "minSubtotal",
    "actions",
  ];

  /** Markets this vendor may list products in (the ISO2 checkboxes). */
  allowedMarkets = signal<string[]>([]);
  /** Markets whose checkboxes are being saved, each held disabled meanwhile. */
  savingMarkets = signal<Set<string>>(new Set());

  error = signal<string>("");
  success = signal<string>("");

  /** Zoned markets (US: five, MY: two) price delivery by the customer's state. */
  readonly deliveryZonesFor = deliveryZonesFor;
  readonly zoneControlName = zoneControlName;

  constructor(
    private formBuilder: FormBuilder,
    private requestService: RequestService,
    private appStateService: AppStateService
  ) {}

  /** A vendor being created (vendor_input/0): nothing saved yet. */
  get isNewVendor(): boolean {
    return !Number(this.vendorId);
  }

  ngOnInit() {
    this.initializeDeliveryRows();
    if (this.isNewVendor) this.showNewVendorDefaults();
    else {
      this.getVendorDeliveryInfo();
      this.getAllowedMarkets();
    }
  }

  /**
   * What a new vendor starts with - set by the database when the vendor is
   * created (db-changes 0087): each live market's default delivery fee from
   * Country (e.g. SG $10, every US zone $50). Shown here so the form does not
   * suggest 0; editable once the vendor exists. Markets only open for listing
   * get no row until the vendor is allowed there, so they stay as they are.
   */
  private showNewVendorDefaults() {
    for (const row of this.deliveryRows()) {
      const fee = this.appStateService
        .liveCountries()
        .find((c) => c.iso2 === row.iso2)?.default_delivery_fee;
      if (fee === null || fee === undefined) continue;
      const zones = deliveryZonesFor(row.iso2);
      if (zones.length > 0) {
        for (const z of zones) row.formGroup.get(zoneControlName(z.zone))?.setValue(fee);
      } else {
        row.formGroup.patchValue({ standardDeliveryFee: fee });
      }
    }
  }

  private initializeDeliveryRows() {
    this.deliveryRows.set(
      this.destinationCountries().map((country) => ({
        iso2: country.iso2,
        formGroup: this.createDeliveryFormGroup(country.iso2),
      }))
    );
  }

  private createDeliveryFormGroup(iso2: string, data?: Partial<IVendorDeliveryInfo>): FormGroup {
    // Declared as a plain FormGroup: zoned rows gain zone controls below, which a
    // strictly-typed group would reject by name.
    const group: FormGroup = this.formBuilder.group({
      deliveryText: [data?.deliveryText, Validators.required],
      standardDeliveryFee: [
        data?.standardDeliveryFee || 0,
        [Validators.required, Validators.min(0), Validators.pattern(decimalNumber)],
      ],
      minSubtotalEnabled: [data?.minSubtotalEnabled ?? false],
      minSubtotalValue: [data?.minSubtotalValue || 0],
      // Optional and free-form: it is the vendor's own arrangement, and leaving
      // it blank is how a vendor says they do not offer collection at all.
      walkInText: [data?.walkInText ?? ""],
      deliveryMode: [data?.deliveryMode ?? "fishyhub"],
    });
    // minSubtotalValue is only required (and must be a positive number) while the
    // Yes/No toggle is on — re-applied whenever the toggle changes, including when
    // patched from saved data below.
    const minSubtotalValueControl = group.get("minSubtotalValue")!;
    group.get("minSubtotalEnabled")?.valueChanges.subscribe((enabled) => {
      minSubtotalValueControl.setValidators(
        enabled
          ? [Validators.required, Validators.min(0.01), Validators.pattern(decimalNumber)]
          : [Validators.min(0), Validators.pattern(decimalNumber)]
      );
      minSubtotalValueControl.updateValueAndValidity();
    });

    // A zoned market (US: five, MY: two) replaces the single fee with zone fees. The
    // single fee is kept in the form, hidden, and held at the highest zone so the
    // existing save path and every reader that predates zones still see a sensible value.
    const zones = deliveryZonesFor(iso2);
    if (zones.length > 0) {
      const feeValidators = [
        Validators.required,
        Validators.min(0),
        Validators.pattern(decimalNumber),
      ];
      for (const z of zones) {
        group.addControl(zoneControlName(z.zone), this.formBuilder.control(0, feeValidators));
      }
      const syncSingleFee = () =>
        group
          .get("standardDeliveryFee")
          ?.setValue(highestZoneFee(this.zoneFeesOf(iso2, group)), { emitEvent: false });
      for (const z of zones) {
        group.get(zoneControlName(z.zone))?.valueChanges.subscribe(syncSingleFee);
      }
    }

    return group;
  }

  /** The zone controls' current values as {"1": n, ..., "n": n}. */
  private zoneFeesOf(iso2: string, group: FormGroup): ZoneFees {
    const fees: ZoneFees = {};
    for (const z of deliveryZonesFor(iso2)) {
      fees[String(z.zone) as keyof ZoneFees] =
        Number(group.get(zoneControlName(z.zone))?.value) || 0;
    }
    return fees;
  }

  private getVendorDeliveryInfo() {
    this.requestService
      .get<IApiResponse>(`/api/v2/vendor-delivery/${this.vendorId}`)
      .subscribe((res) => {
        if (res.status && res.data) {
          const deliveryInfo = res.data as IVendorDeliveryInfo[];
          const iso2sWithDeliveryInfo = new Set(deliveryInfo.map((info) => info.iso2));

          // Reorder rows to put countries with delivery info on top
          this.deliveryRows().sort((a, b) => {
            const aHasInfo = iso2sWithDeliveryInfo.has(a.iso2);
            const bHasInfo = iso2sWithDeliveryInfo.has(b.iso2);
            if (aHasInfo === bHasInfo) {
              return a.iso2.localeCompare(b.iso2);
            }
            return aHasInfo ? -1 : 1;
          });

          // Update form groups with existing data
          deliveryInfo.forEach((info) => {
            const row = this.deliveryRows().find((r) => r.iso2 === info.iso2);
            if (row) {
              row.formGroup.patchValue({
                deliveryText: info.deliveryText,
                standardDeliveryFee: info.standardDeliveryFee,
                minSubtotalEnabled: info.minSubtotalEnabled ?? false,
                minSubtotalValue: info.minSubtotalValue || 0,
                walkInText: info.walkInText ?? "",
                deliveryMode: info.deliveryMode ?? "fishyhub",
              });
              // Zones (US, MY): saved values where present; a vendor that predates zones
              // starts every zone at its existing single fee, so saving without
              // editing reproduces exactly what customers pay today.
              for (const z of deliveryZonesFor(info.iso2)) {
                const saved = info.deliveryZoneFees?.[String(z.zone) as keyof ZoneFees];
                row.formGroup
                  .get(zoneControlName(z.zone))
                  ?.setValue(saved ?? info.standardDeliveryFee ?? 0);
              }
            }
          });
        }
      });
  }

  private getAllowedMarkets() {
    this.requestService.get<IApiResponse>(`/api/v2/vendor/${this.vendorId}/markets`).subscribe({
      next: (res) => {
        if (res.status && res.data) {
          this.allowedMarkets.set((res.data as { allowedMarkets: string[] }).allowedMarkets ?? []);
        } else {
          this.error.set(res.message || "Failed to load the vendor's markets");
          setTimeout(() => this.error.set(""), 3000);
        }
      },
      error: () => {
        this.error.set("Failed to load the vendor's markets");
        setTimeout(() => this.error.set(""), 3000);
      },
    });
  }

  /** Ticks or unticks one market, leaving the others as they are. */
  private setMarketTicked(iso2: string, ticked: boolean) {
    const rest = this.allowedMarkets().filter((m) => m !== iso2);
    this.allowedMarkets.set(ticked ? [...rest, iso2] : rest);
  }

  private markSaving(iso2: string, saving: boolean) {
    this.savingMarkets.update((current) => {
      const next = new Set(current);
      if (saving) next.add(iso2);
      else next.delete(iso2);
      return next;
    });
  }

  /**
   * A market's checkbox: saved at once; on failure only that box goes back.
   * Several markets can be saving at the same time.
   */
  setMarketAllowed(iso2: string, allowed: boolean) {
    this.setMarketTicked(iso2, allowed);
    this.markSaving(iso2, true);
    this.requestService
      .put<IApiResponse>(`/api/v2/vendor/${this.vendorId}/markets`, { iso2, allowed })
      .subscribe({
        next: (res) => {
          this.markSaving(iso2, false);
          if (res.status && res.data) {
            // The saved list, except markets still saving keep what was clicked.
            const saving = this.savingMarkets();
            const saved = (res.data as { allowedMarkets: string[] }).allowedMarkets ?? [];
            this.allowedMarkets.set([
              ...saved.filter((m) => !saving.has(m)),
              ...this.allowedMarkets().filter((m) => saving.has(m)),
            ]);
            this.success.set(`${iso2} ${allowed ? "allowed" : "blocked"} for this vendor`);
            setTimeout(() => this.success.set(""), 3000);
          } else {
            this.setMarketTicked(iso2, !allowed);
            this.error.set(res.message || `Failed to save ${iso2}`);
            setTimeout(() => this.error.set(""), 3000);
          }
        },
        error: () => {
          this.markSaving(iso2, false);
          this.setMarketTicked(iso2, !allowed);
          this.error.set(`Failed to save ${iso2}`);
          setTimeout(() => this.error.set(""), 3000);
        },
      });
  }

  saveVendorDeliveryInfo(iso2: string) {
    const row = this.deliveryRows().find((r) => r.iso2 === iso2);
    if (!row || row.formGroup.invalid) return;

    const confirmSave = window.confirm("Are you sure you want to save this delivery information?");
    if (!confirmSave) return;

    const deliveryInfo: IVendorDeliveryInfo = {
      iso2,
      ...row.formGroup.value,
      ...(deliveryZonesFor(iso2).length > 0
        ? { deliveryZoneFees: this.zoneFeesOf(iso2, row.formGroup) }
        : {}),
    };

    this.requestService
      .post<IApiResponse>(`/api/v2/vendor-delivery/${this.vendorId}`, deliveryInfo)
      .subscribe((res) => {
        if (res.status) {
          this.getVendorDeliveryInfo();
          this.success.set("Delivery information saved successfully");
          setTimeout(() => this.success.set(""), 3000);
        } else {
          this.error.set(res.message || "Failed to save delivery information");
          setTimeout(() => this.error.set(""), 3000);
        }
      });
  }
}
